
import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Row, Col, Card, Button, Typography, Descriptions, Tag,
  notification, Spin, Avatar, Divider, Timeline, Space,
  Modal, Drawer, Input, Form, Table as AntTable
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  ArrowLeftOutlined, EditOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined,
  UserOutlined, BookOutlined, BankOutlined, FileTextOutlined,
  DownloadOutlined, AuditOutlined, FilePdfOutlined,
} from "@ant-design/icons";
import moment from 'moment';
import { hasPermission } from '../../components/permissions';
import ExpendituresForm from './Form';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

function ExpendituresView() {
  const { id } = useParams();
  const history = useHistory();
  const [expenditure, setExpenditure] = useState(null);
  const [approvalWorkflow, setApprovalWorkflow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [form] = Form.useForm();
  
  // Edit Modal States
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentExpenditureId, setCurrentExpenditureId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  
  // Dropdown Data States
  const [beneficiariesList, setBeneficiariesList] = useState([]);
  const [purposesList, setPurposesList] = useState([]);

  useEffect(() => {
    fetchExpenditureDetails();
    fetchApprovalWorkflow();
    loadDropdownData(); // Add this line
  }, [id]);

  // ADD THIS FUNCTION - This was missing!
  const loadDropdownData = async () => {
    try {
      const [beneficiariesRes, purposesRes] = await Promise.all([
        axios.get(`${baseUrl}/beneficiaries/list?limit=all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
        }),
        axios.get(`${baseUrl}/purposes/list?limit=all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
        })
      ]);
      
      setBeneficiariesList(beneficiariesRes.data.result || []);
      setPurposesList(purposesRes.data.result || []);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    }
  };

  const fetchExpenditureDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/expenditures/read/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        setExpenditure(response.data.result);
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch expenditure details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalWorkflow = async () => {
    try {
      const response = await axios.get(`${baseUrl}/approval-workflow/expenditure/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        setApprovalWorkflow(response.data.result);
      }
    } catch (error) {
      console.error('Failed to fetch approval workflow:', error);
    }
  };

  const handleApprovalAction = async (values) => {
    try {
      const endpoint = actionType === 'approve' ? 'approve' : 'reject';
      const response = await axios.patch(`${baseUrl}/expenditures/${endpoint}/${id}`, {
        comments: values.comments || `Expenditure ${actionType}d`
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: `Expenditure ${actionType}d successfully!`,
          placement: 'topRight'
        });
        
        setActionModalVisible(false);
        form.resetFields();
        fetchExpenditureDetails();
        fetchApprovalWorkflow();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: `Failed to ${actionType} expenditure`,
        placement: 'topRight'
      });
    }
  };

  const showActionModal = (type) => {
    setActionType(type);
    setActionModalVisible(true);
  };

  // FIX THIS FUNCTION - Update the showDrawer function
  const showDrawer = (expenditureId = null) => {
    setCurrentExpenditureId(expenditureId);
    setIsUpdateMode(!!expenditureId);
    setDrawerVisible(true);
  };

  // ADD THIS FUNCTION - For closing the drawer
  const closeDrawer = () => {
    setDrawerVisible(false);
    setCurrentExpenditureId(null);
    setIsUpdateMode(false);
  };

  // ADD THIS FUNCTION - For refreshing data after edit
  const onRefresh = () => {
    closeDrawer();
    fetchExpenditureDetails();
    fetchApprovalWorkflow();
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'warning',
      'Approved': 'success',
      'Rejected': 'error',
      'Cancelled': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': <CalendarOutlined />,
      'Approved': <CheckCircleOutlined />,
      'Rejected': <CloseCircleOutlined />,
      'Cancelled': <CloseCircleOutlined />
    };
    return icons[status];
  };

  const getWorkflowColor = (action) => {
    const colors = {
      'Submitted': 'blue',
      'Approved': 'green',
      'Rejected': 'red',
      'Cancelled': 'default'
    };
    return colors[action] || 'default';
  };

  const handleDownloadDocument = (documentPath, documentType) => {
    if (!documentPath) return;
    // invoice_document_path may be a JSON array of URLs
    try {
      const parsed = JSON.parse(documentPath);
      if (Array.isArray(parsed)) {
        parsed.forEach((url, i) => setTimeout(() => window.open(url, '_blank'), i * 300));
        return;
      }
    } catch { /* not JSON — treat as single URL */ }
    window.open(documentPath, '_blank');
  };

  const handleGenerateInvoice = async () => {
    setGeneratingInvoice(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${baseUrl}/expenditures/generate-invoice/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `voucher-${expenditure.txn_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Delay revocation so browser completes the download before URL is freed
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      notification.error({ message: 'Invoice generation failed', description: err.message });
    } finally {
      setGeneratingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!expenditure) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text>Expenditure not found</Text>
      </div>
    );
  }

  const totalAmount = (parseFloat(expenditure.amount) + parseFloat(expenditure.tax_amount || 0));

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Button 
                  icon={<ArrowLeftOutlined />} 
                  onClick={() => history.goBack()}
                  size="large"
                />
                <div>
                  <Title level={2} style={{ margin: 0 }}>{expenditure.txn_id}</Title>
                  <div style={{ marginTop: '8px' }}>
                    <Tag color={getStatusColor(expenditure.approval_status)} icon={getStatusIcon(expenditure.approval_status)}>
                      {expenditure.approval_status}
                    </Tag>
                    <Tag color="blue">{expenditure.fiscal_year}</Tag>
                  </div>
                </div>
              </div>
              <Space wrap>
                {expenditure.approval_status === 'Pending' && hasPermission('/expenditures', 'editPermission') && (
                  <>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => showActionModal('approve')}
                      size="large"
                      style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Approve
                    </Button>
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => showActionModal('reject')}
                      size="large"
                    >
                      Reject
                    </Button>
                  </>
                )}
                <Button
                  icon={<FilePdfOutlined />}
                  loading={generatingInvoice}
                  onClick={handleGenerateInvoice}
                  size="large"
                  style={{ background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}
                >
                  Download Voucher
                </Button>
                {hasPermission('/expenditures', 'editPermission') && (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => showDrawer(expenditure.id)}
                    size="large"
                    style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
                  >
                    Edit
                  </Button>
                )}
              </Space>
            </div>

            {/* Your existing card content here... */}
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <Card title="Transaction Details" size="small">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item 
                      label={<>&#8377; Transaction ID</>}
                    >
                      <Text strong style={{ color: '#1890ff' }}>{expenditure.txn_id}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label={<><CalendarOutlined /> Transaction Date</>}
                    >
                      {moment(expenditure.txn_date).format('DD MMM YYYY')}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Amount"
                    >
                      <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                        ₹{Number(expenditure.amount).toLocaleString('en-IN')}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Tax Amount"
                    >
                      ₹{Number(expenditure.tax_amount || 0).toLocaleString('en-IN')}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Total Amount"
                    >
                      <Text strong style={{ color: '#f5222d', fontSize: '18px' }}>
                        ₹{totalAmount.toLocaleString('en-IN')}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Currency"
                    >
                      {expenditure.currency || 'INR'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Fiscal Year"
                    >
                      <Tag color="blue">{expenditure.fiscal_year}</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

               <Col xs={24} lg={12}>
                 <Card title="Beneficiary & Purpose" size="small">
                   <Descriptions column={1} bordered size="small">
                     <Descriptions.Item 
                      label={<><UserOutlined /> Beneficiary</>}
                    >
                      <div>
                        <Text strong>{expenditure.beneficiary_name}</Text>
                        <br />
                        <Tag color="blue" size="small">{expenditure.beneficiary_type}</Tag>
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Contact"
                    >
                      <div>
                        {expenditure.beneficiary_phone && (
                          <div>📞 {expenditure.beneficiary_phone}</div>
                        )}
                        {expenditure.beneficiary_email && (
                          <div>📧 {expenditure.beneficiary_email}</div>
                        )}
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label={<><BookOutlined /> Purpose</>}
                    >
                      <div>
                        <Text strong>{expenditure.purpose_name}</Text>
                        <br />
                        <Text type="secondary">Code: {expenditure.purpose_code}</Text>
                        <br />
                        <Tag color="orange" size="small">{expenditure.purpose_category}</Tag>
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card title="Payment Details" size="small">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item 
                      label={<><BankOutlined /> Payment Mode</>}
                    >
                      <Tag color="processing">{expenditure.payment_mode}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Cheque Number"
                    >
                      {expenditure.cheque_no || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Cheque Date"
                    >
                      {expenditure.cheque_date ? moment(expenditure.cheque_date).format('DD MMM YYYY') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="UTR Number"
                    >
                      {expenditure.utr_no || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Transaction Reference"
                    >
                      {expenditure.transaction_ref || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Invoice Number"
                    >
                      {expenditure.invoice_no || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card title="Additional Information" size="small">
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item 
                      label="Remarks"
                    >
                      {expenditure.remarks || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Internal Notes"
                    >
                      {expenditure.internal_notes || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label="Approved By"
                    >
                      {expenditure.approved_by_name || expenditure.approved_by || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Approved At"
                    >
                      {expenditure.approved_at ? moment(expenditure.approved_at).format('DD MMM YYYY, HH:mm') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Created On"
                    >
                      {moment(expenditure.created_at).format('DD MMM YYYY, HH:mm')}
                    </Descriptions.Item>
                    <Descriptions.Item 
                      label="Last Updated"
                    >
                      {moment(expenditure.updated_at).format('DD MMM YYYY, HH:mm')}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Line Items Section */}
              <Col span={24}>
                {(() => {
                  let lineItems = [];
                  try { lineItems = expenditure.line_items ? JSON.parse(expenditure.line_items) : []; } catch(_) {}

                  let subtotal = 0, taxTotal = 0;
                  const rows = lineItems.map((item, idx) => {
                    const s = (item.qty || 0) * (item.rate || 0);
                    const t = s * (item.taxPct || 0) / 100;
                    subtotal += s;
                    taxTotal += t;
                    return { key: idx + 1, no: idx + 1, description: item.description || '—', qty: item.qty, rate: item.rate, taxPct: item.taxPct || 0, amount: s + t };
                  });
                  const grandTotal = subtotal + taxTotal;

                  if (lineItems.length === 0) {
                    // Legacy: single row from amount/tax_amount
                    const amt = parseFloat(expenditure.amount || 0);
                    const tax = parseFloat(expenditure.tax_amount || 0);
                    rows.push({ key: 1, no: 1, description: expenditure.remarks || 'Expenditure', qty: 1, rate: amt, taxPct: 0, amount: amt + tax });
                    subtotal = amt;
                    taxTotal = tax;
                  }

                  const lineColumns = [
                    { title: '#', dataIndex: 'no', key: 'no', width: 50, align: 'center' },
                    { title: 'Description', dataIndex: 'description', key: 'description' },
                    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 70, align: 'center' },
                    {
                      title: 'Rate', dataIndex: 'rate', key: 'rate', width: 130, align: 'right',
                      render: v => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                    },
                    { title: 'Tax %', dataIndex: 'taxPct', key: 'taxPct', width: 80, align: 'center', render: v => `${v}%` },
                    {
                      title: 'Amount', dataIndex: 'amount', key: 'amount', width: 140, align: 'right',
                      render: v => <Text strong style={{ color: '#7a0000' }}>₹{Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text>
                    },
                  ];

                  return (
                    <Card
                      title={<span style={{ fontWeight: 600 }}>₹ Line Items</span>}
                      size="small"
                    >
                      <AntTable
                        columns={lineColumns}
                        dataSource={rows}
                        pagination={false}
                        size="small"
                        bordered
                        footer={() => (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ marginBottom: 4 }}>
                              <Text type="secondary">Subtotal: </Text>
                              <Text strong>₹{Number(subtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text>
                            </div>
                            {taxTotal > 0 && (
                              <div style={{ marginBottom: 4 }}>
                                <Text type="secondary">Tax: </Text>
                                <Text strong>₹{Number(taxTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text>
                              </div>
                            )}
                            <div style={{ fontSize: 16, borderTop: '2px solid #7a0000', paddingTop: 6, marginTop: 4 }}>
                              <Text style={{ color: '#7a0000', fontWeight: 800 }}>Grand Total: ₹{Number(subtotal + taxTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text>
                            </div>
                          </div>
                        )}
                      />
                    </Card>
                  );
                })()}
              </Col>

              {/* Documents Section */}
              <Col span={24}>
                <Card title="Documents" size="small">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <div style={{ border: '1px dashed #d9d9d9', padding: '16px', textAlign: 'center', borderRadius: '6px' }}>
                        <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                        <div style={{ marginTop: '8px' }}>
                          <Text strong>Proof Document</Text>
                          <br />
                          {expenditure.proof_document_path ? (
                            <Button 
                              type="link" 
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownloadDocument(expenditure.proof_document_path, 'Proof')}
                            >
                              Download
                            </Button>
                          ) : (
                            <Text type="secondary">No document uploaded</Text>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ border: '1px dashed #d9d9d9', padding: '16px', textAlign: 'center', borderRadius: '6px' }}>
                        <FileTextOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                        <div style={{ marginTop: '8px' }}>
                          <Text strong>Invoice Documents</Text>
                          <br />
                          {expenditure.invoice_document_path ? (
                            <Button 
                              type="link" 
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownloadDocument(expenditure.invoice_document_path, 'Invoice')}
                            >
                              Download All
                            </Button>
                          ) : (
                            <Text type="secondary">No documents uploaded</Text>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>

              {/* Approval Workflow */}
              <Col span={24}>
                <Card 
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AuditOutlined />
                      Approval Workflow
                    </div>
                  } 
                  size="small"
                >
                  {approvalWorkflow.length > 0 ? (
                    <Timeline mode="left">
                      {approvalWorkflow.map((item, index) => (
                        <Timeline.Item
                          key={index}
                          color={getWorkflowColor(item.action)}
                          label={moment(item.action_date).format('DD MMM YYYY, HH:mm')}
                        >
                          <div>
                            <Text strong>{item.action}</Text> by {item.approver_name}
                            {item.comments && (
                              <div style={{ marginTop: '4px' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  "{item.comments}"
                                </Text>
                              </div>
                            )}
                          </div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  ) : (
                    <Text type="secondary">No workflow history available</Text>
                  )}
                </Card>
              </Col>
              
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Action Modal */}
      <Modal
        title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Expenditure`}
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={form} onFinish={handleApprovalAction} layout="vertical">
          <Form.Item
            label="Comments"
            name="comments"
            rules={[{ required: actionType === 'reject', message: 'Please provide reason for rejection' }]}
          >
            <TextArea
              placeholder={`Enter comments for ${actionType}...`}
              rows={4}
            />
          </Form.Item>
          <div style={{ textAlign: 'right', marginTop: '20px' }}>
            <Space>
              <Button onClick={() => {
                setActionModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                danger={actionType === 'reject'}
                style={actionType === 'approve' ? { background: '#52c41a', borderColor: '#52c41a' } : {}}
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'} Expenditure
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Edit Drawer */}
      <Drawer
        title={<span style={{ fontWeight: 700, color: '#1e293b' }}>Update Expenditure</span>}
        width="88vw"
        headerStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0'}}
        closeIcon={<span style={{ color: '#1e293b', fontSize: 16 }}>✕</span>}
        open={drawerVisible}
        onClose={closeDrawer}
        destroyOnClose
      >
        <ExpendituresForm
          expenditureId={currentExpenditureId}
          onRefreshData={onRefresh}
          beneficiariesList={beneficiariesList}
          purposesList={purposesList}
        />
      </Drawer>
    </div>
  );
}

export default ExpendituresView;